import { Box } from '@mui/material';
import { useRef, useState } from 'react';

/**
 * @typedef {Object} SecureEmailProps
 * @property {string} emailKey - Key to identify which email to use
 * @property {boolean} [showAsLink=true] - Whether to render as a clickable mailto link
 * @property {Object} [sx] - MUI sx prop for styling
 * @property {React.ReactNode} [children] - Optional children to display instead of email
 * @property {string} [component='a'] - The component to render as (a, span, div, etc.)
 * @property {string} [fallbackText] - Text to show if JavaScript is disabled
 */

// Hardcoded obfuscated email data - never store full emails here
const EMAIL_DATA = {
  info: {
    // "info" reversed
    a: 'ofni'.split('').join(''),
    // "ellafin" reversed
    b: 'nifalle'.split('').join(''),
    // "ai" reversed
    c: 'ia'.split('').join(''),
    // Extra obfuscated parts for verification
    x: 'aW5m', // base64 fragment
    y: 'ZWxs', // base64 fragment
  },
  sales: {
    // "sales" reversed
    a: 'selas'.split('').join(''),
    // "ellafin" reversed
    b: 'nifalle'.split('').join(''),
    // "ai" reversed
    c: 'ia'.split('').join(''),
    x: 'c2Fs', // base64 fragment
    y: 'ZWxs', // base64 fragment
  },
  support: {
    // "support" reversed
    a: 'troppus'.split('').join(''),
    // "ellafin" reversed
    b: 'nifalle'.split('').join(''),
    // "ai" reversed
    c: 'ia'.split('').join(''),
    x: 'c3Vw', // base64 fragment
    y: 'ZWxs', // base64 fragment
  },
};

/**
 * Secure email component that reconstructs email addresses client-side
 * only after user interaction, preventing automated scraping.
 *
 * @param {SecureEmailProps} props - Component props
 * @returns {JSX.Element} Secure email component
 */
function SecureEmail({
  emailKey,
  showAsLink = true,
  sx = {},
  children,
  component = 'a',
  fallbackText = 'Contact us (JavaScript required)',
  ...otherProps
}) {
  const [decodedEmail, setDecodedEmail] = useState('');
  const [isRevealed, setIsRevealed] = useState(false);
  const elementRef = useRef(null);

  const revealEmail = () => {
    const emailData = EMAIL_DATA[emailKey];
    if (!emailData) {
      // eslint-disable-next-line no-console
      console.warn(`Unknown email key: ${emailKey}`);
      return;
    }

    // Reconstruct email client-side only after user interaction
    const localPart = emailData.a.split('').reverse().join('');
    const domain = emailData.b.split('').reverse().join('');
    const tld = emailData.c.split('').reverse().join('');

    const reconstructed = `${localPart}@${domain}.${tld}`;

    setDecodedEmail(reconstructed);
    setIsRevealed(true);

    // Set up the mailto link
    if (showAsLink && elementRef.current) {
      elementRef.current.href = `mailto:${reconstructed}`;
      elementRef.current.setAttribute(
        'aria-label',
        `Send email to ${reconstructed}`
      );
    }
  };

  // Props for the component
  const componentProps = {
    ref: elementRef,
    sx: {
      color: 'inherit',
      textDecoration: showAsLink ? 'underline' : 'none',
      cursor: 'pointer',
      ...sx,
    },
    // WCAG: Proper role and aria attributes
    role: 'button',
    tabIndex: 0,
    onClick:
      isRevealed && showAsLink && decodedEmail
        ? () => (window.location.href = `mailto:${decodedEmail}`)
        : revealEmail,
    'aria-label': isRevealed
      ? `Send email to ${decodedEmail}`
      : 'Click To Email',
    ...otherProps,
  };

  // Handle keyboard activation for accessibility
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isRevealed && showAsLink && decodedEmail) {
        window.location.href = `mailto:${decodedEmail}`;
      } else {
        revealEmail();
      }
    }
  };

  if (showAsLink) {
    return (
      <Box component={component} {...componentProps} onKeyDown={handleKeyDown}>
        <noscript>
          {/* Fallback for users without JavaScript */}
          <span style={{ fontStyle: 'italic', opacity: 0.8 }}>
            {fallbackText}
          </span>
        </noscript>
        {!isRevealed && (
          <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>
            {children || 'Click To Email'}
          </span>
        )}
        {isRevealed && <>{decodedEmail}</>}
      </Box>
    );
  }

  // For non-link display (just text)
  return (
    <Box component={component} {...componentProps} onKeyDown={handleKeyDown}>
      <noscript>{fallbackText}</noscript>
      {!isRevealed && (
        <span style={{ cursor: 'pointer' }}>
          {children || 'Click To Email'}
        </span>
      )}
      {isRevealed && decodedEmail}
    </Box>
  );
}

export default SecureEmail;
